import { ApiClient, ApiResponse, ServiceBase } from './api'

// 主提单状态枚举
export enum MasterBillStatus {
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
  AWAITING_DEVAN = 'AWAITING_DEVAN',
  DEVANNING = 'DEVANNING',
  MOVED_IN = 'MOVED_IN',
  MOVED_OUT_FINAL = 'MOVED_OUT_FINAL'
}

// 状态显示名称映射
export const MasterBillStatusDisplay: Record<MasterBillStatus, string> = {
  [MasterBillStatus.PENDING_CONFIRMATION]: '待确认',
  [MasterBillStatus.AWAITING_DEVAN]: '待拆柜',
  [MasterBillStatus.DEVANNING]: '拆柜中',
  [MasterBillStatus.MOVED_IN]: '已搬入',
  [MasterBillStatus.MOVED_OUT_FINAL]: '已搬出'
}

export interface MasterBillDate {
  devanDate?: string // 拆柜日期 (YYYY-MM-DD format)
  inboundDate?: string // 预计入库日期 (YYYY-MM-DD format)
  freetimeDate?: string // Freetime日期 (YYYY-MM-DD format)
}

// API 响应数据结构（驼峰命名）
// - 船舶和航次信息：vesselName, voyageNumber
// - 港口信息：portOfLoading, portOfDischarge, placeOfReceipt, placeOfDelivery
// - 集装箱信息：containerNumber, containerType, containerSize
// - 提单信息：masterBlNumber, customerNumber, status
// - 货物信息：totalPackages, totalWeight, totalVolume, totalUnit
// - 日期信息：billDate, devanDate, inboundDate, freetimeDate
export interface MasterBillSchema {
  vesselName?: string
  voyageNumber?: string
  portOfLoading?: string
  portOfDischarge?: string
  placeOfReceipt?: string
  placeOfDelivery?: string
  containerNumber?: string
  containerType?: string
  containerSize?: string
  masterBlNumber: string
  customerNumber?: string
  status?: MasterBillStatus
  statusDisplay?: string
  totalPackages?: number
  totalWeight?: number
  totalVolume?: number
  totalUnit?: string
  billDate?: string
  devanDate?: string | null
  inboundDate?: string | null
  freetimeDate?: string | null
  id: number
  houseBills?: HouseBillSchema[]
}

// 数据类型定义
export interface MasterBill extends MasterBillSchema {
  id: number
  created_at: string
  updated_at: string
}

export interface HouseBillSchema {
  id: number
  houseBlNumber: string
  sequenceNumber: number
  originalShipper?: string
  originalConsignee?: string
  originalNotifyParty?: string
  cargoDescription?: string
  packages?: number
  weight?: number
  volume?: number
  weightKg?: number
  volumeCbm?: number
  shipperDetails?: string
  marksNumbers?: string[]
  naccsPermitStatus?: boolean
  importPermitStatus?: boolean
  scheduledOutboundDate?: string // 预约搬出日期 (YYYY-MM-DD format)
  createdAt: string
  updatedAt: string
  note: string
  status: string
  statusDisplay: string
  isFullQuantity: boolean
  isInspectionRequired: boolean
  isReturnWarehouse: boolean
  deliveryOpt: string
  deliveryNote: string

  masterBillId: number
  masterBillNumber: string
  masterBill?: MasterBill
  cargoDetails?: CargoMarkSchema[]
  cargoMarks: CargoMarkSchema[]
}

export interface CargoMarkSchema {
  id: number
  cargoNumber?: string
  sequenceNumber: number
  packages?: number
  createdAt: string
  updatedAt: string
  houseBillId: number
}

export interface UnpackingDetailView {
  cargoDetailId: number
  houseBillId: number
  masterBlNumber?: string
  houseBlNumber?: string
  cargoNumber?: string
  packages?: number
  goodsDescription?: string
  naccsPermitStatus?: boolean
  importPermitStatus?: boolean
  inspectionStatus?: string
  inspectionDate?: string
  deliveryScheduledDate?: string
  deliveryCompletedDate?: string
  deliveryMethod?: string
  operationNotes?: string
  operator?: string
  createdAt?: string
  updatedAt?: string
}

export interface ExcelImportRequest {
  fileData: string
  filename: string
}

export interface ExcelImportResponse {
  success: boolean
  message: string
  errors: string[]
  data?: {
    masterBillId: number
  }
}

// 仓库进度相关接口
export interface BoxStatistics {
  total: number
  pending: number
  picked: number
  palletized: number
  warehoused: number
}

export interface PalletInfo {
  palletId: number
  palletCode: string
  sequenceNumber: number
  boxCount: number
  createdAt: string
  totalWeight: number
  totalVolume: number
}

export interface MarkProgress {
  markNumber: string
  markIndex: number
  status: 'in_progress' | 'completed' | 'pending'
  boxes: BoxStatistics
  progressPercentage: number
}

export interface MarksSummary {
  totalMarks: number
  completedMarks: number
  completionPercentage: number
}

export interface HouseBillProgress {
  houseBillId: number
  houseBlNumber: string
  totalMarks: number
  boxes: BoxStatistics
  progressPercentage: number
  pallets: PalletInfo[]
  marksSummary: MarksSummary
  marksList: MarkProgress[]
}

export interface MasterBillProgress {
  masterBillId: number
  masterBlNumber: string
  containerNumber?: string
  vesselName?: string
  voyageNumber?: string
  boxes: BoxStatistics
  progressPercentage: number
  houseBills: HouseBillProgress[]
}

export interface PalletLabelData {
  palletId: number
  palletCode: string
  houseBlNumber: string
  totalBoxes: number
  totalMarks: number
  sequenceNumber: number
  currentPalletBoxes: number
  totalWeight: number
  totalVolume: number
  createdAt: string
  operator?: string
}

export class CustomsV2Service extends ServiceBase {
  constructor(client: ApiClient) {
    super(client, '/v2/customs')
  }

  // #1 Excel导入
  async importExcel(request: ExcelImportRequest) {
    return this.post<ExcelImportResponse>('/import', request)
  }

  // #2 Excel 导出-货物模板
  async exportExcel(masterBillId: number): Promise<Blob> {
    const endpoint = `/export/${masterBillId}`
    return this.download(endpoint)
  }

  // #3 主提单管理
  async getMasterBills() {
    return this.get<ApiResponse<MasterBillSchema[]>>('/master-bills')
  }

  // #4 主提单详情
  async getMasterBill(id: number) {
    return this.get<ApiResponse<MasterBillSchema>>(`/master-bill/${id}`)
  }

  // #5 确认主提单-更新日期
  async updateMasterBillDate(id: number, data: MasterBillDate) {
    return this.put<ApiResponse<MasterBillSchema>>(
      `/master-bill/${id}/date`,
      data
    )
  }

  // #6 确认主提单-变更状态（待确认-待拆柜）
  async updateMasterBillStatus(id: number, status: MasterBillStatus) {
    return this.put<ApiResponse<MasterBillSchema>>(
      `/master-bill/${id}/status`,
      { status }
    )
  }

  // #7 删除主提单（级联删除，所有相关表全部物理删除）
  async deleteMasterBill(id: number) {
    return this.delete<ApiResponse>(`/master-bill/${id}`)
  }

  // #8 子提单归档
  async archive(houseBillId: number) {
    return this.put<ApiResponse>(`/house-bill/${houseBillId}/complete`)
  }

  // #9 子提单查验
  async inspect(houseBillId: number) {
    return this.put<ApiResponse>(`/house-bill/${houseBillId}/inspect`)
  }

  // #10 取消查验
  async inspectCancel(houseBillId: number) {
    return this.put<ApiResponse>(`/house-bill/${houseBillId}/inspect-cancel`)
  }

  // #11 更新备注
  async changeNote(houseBillId: number, note: string) {
    return this.put<ApiResponse<HouseBillSchema>>(
      `/house-bill/${houseBillId}/note`,
      { note }
    )
  }

  // #12 子提单管理
  async fetchHouseBills() {
    return this.get<ApiResponse<HouseBillSchema[]>>(`/house-bills`)
  }
}

export default CustomsV2Service
